import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import * as StoreModel from '../../../model/ngrx';
import { Store } from '@ngrx/store';
import { AppState } from '../../../store/state/app.state';
import { ConfigLoaderService } from '../../services/api-services/configLoader.service';
import { quoteLevelConstants } from '../../constants/attribute-values.constants';

@Injectable({
    providedIn: 'root'
})
export class CommonFunctionsService {
    public fundDetails: StoreModel.YourPension;
    private fundDetailsStore: Observable<StoreModel.YourPension[]>;

    constructor(private store: Store<AppState>, private configLoaderService: ConfigLoaderService) { }
    enableTwoStageGuarantee(): boolean {
        const configDate = this.configLoaderService.configuration.quoteCreationCheckDate;
        // configDate format expected: DD/MM/YYYY
        const [day, month, year] = configDate.split('/').map(Number);
        const targetDate = new Date(year, month - 1, day);
        const today = new Date();
        // Normalise both dates to midnight
        targetDate.setHours(0, 0, 0, 0);
        today.setHours(0, 0, 0, 0);
        return today > targetDate;
    }
    getQuoteExpiry(quoteExpiry: number, eardDate: string) {
        if (this.configLoaderService.configuration.quoteCreationCheckDate && this.enableTwoStageGuarantee() && this.hasValue(eardDate)) {
            return this.getDaysDifference(eardDate);
        }
        return quoteExpiry;
    }
    getExpiryCount(quoteExpiry) {
        return (quoteExpiry === 0 ? 'today' : quoteExpiry);
    }
    getDaysDifference(expectedDate: string): number {
        if (!expectedDate) return 0;
        // Parse DD/MM/YYYY
        const dmyRegex = /^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/;
        const match = expectedDate.match(dmyRegex);
        if (!match) return 0;

        const [_, d, m, y] = match.map(Number);
        const target = new Date(y, m - 1, d);
        target.setHours(0, 0, 0, 0);

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const msPerDay = 1000 * 60 * 60 * 24;
        const diffDays = Math.floor((target.getTime() - today.getTime()) / msPerDay);

        // Apply business rule:
        // expiryDays = diffDays + 1, clamped between 0 and 15
        let expiryDays = diffDays + 1;
        
        if (expiryDays <= 0 && diffDays < 0) {
            return diffDays; /* already expired */
        } else {
            if (expiryDays > 15) expiryDays = 15;
            if (expiryDays <= 1) expiryDays = 0; /* expires on expectedDate */
        }
        return expiryDays;
    }


    getFormattedDate(dateStr: string): string {
        if (dateStr) {
            // dateStr format: DD/MM/YYYY
            const [day, month, year] = dateStr.split('/').map(Number);
            const date = new Date(year, month - 1, day);
            const options: Intl.DateTimeFormatOptions = {
                day: 'numeric',
                month: 'long',
                year: 'numeric'
            };
            return date.toLocaleDateString('en-GB', options);
        }
        return null;
    }

    displayExpiryText(tileType: string, iterationId, eardDate: string) {
        const isTwoStage = this.enableTwoStageGuarantee();
        const isApplication = tileType === 'application';
        const isPartialApp = String(iterationId)?.includes('PartialApp');
        const isQuote = tileType === 'quote';
        return ((isTwoStage && isApplication && isPartialApp && this.hasValue(eardDate)) || isQuote);
    }

    checkIfExtFundAdded(sourceCode: string, totalExternalFund) {
        if (sourceCode) {
            /* only L&G fund added- Internal else others considered as Ext(including L&G+Ext) */
            return !(sourceCode === quoteLevelConstants.sourceCode.landG && !this.hasValue(totalExternalFund));
        }
        return false;
    }

    formatContent(cmsData: any, expiryDays: number, guaranteeEndDate: string, eardDate: string, isExtFunAdded: boolean, journeyType: string, displayMode: 'toolTip' | 'label') {
        const isTwoStage = this.configLoaderService.configuration.quoteCreationCheckDate && this.enableTwoStageGuarantee() && !!eardDate;
        if (displayMode === 'toolTip') {
            if (isTwoStage) {
                let p1Text = journeyType === 'application' ? cmsData.String_QuoteGuaranteedCopy : cmsData.String_QuoteExpiryDateCopy;
                let p2Text = [cmsData.String_QuoteExtendGuaranteeDate, cmsData.String_QuoteExtensionCopy].join(' ');

                if (expiryDays === 0) {
                    p1Text = p1Text.replace('for <expiryDays> days', 'until the end of today');
                    p2Text = p2Text.replace('within <expiryDays> days', 'today');
                } else {
                    p1Text = p1Text.replace('<expiryDays>', expiryDays.toString());
                    p2Text = p2Text.replace('<expiryDays>', expiryDays.toString());
                }

                const p1 = `${p1Text} ${cmsData.String_NewQuoteCopy}`;
                const p2 = p2Text.replace('<guaranteeEndDate>', this.getFormattedDate(guaranteeEndDate));
                const p3 = cmsData.String_RatesAvailableCopy;

                return isExtFunAdded ? `<p>${p1}</p><p>${p2}</p><p>${p3}</p>` : `<p>${p1}</p>`;
            } else {
                return cmsData.string_QuoteGuaranteeInformationWording_1 + expiryDays + cmsData.string_QuoteGuaranteeInformationWording_2;
            }
        }

        if (displayMode === 'label') {
            let labelText = journeyType === 'application' ? (expiryDays < 0 ? cmsData.String_ExpiredApplication : cmsData.String_Expires) : cmsData.commonBlock.string_QuoteExpiresWording;
            return expiryDays === 0 && isTwoStage ? labelText.replace(/\bin\b/g, '') : labelText;
        }
        return ''; // label fallback
    }

    hasValue(val: any): boolean {
        return val !== null && val !== undefined && val !== '' && val !== 0;
    }
}